import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareReleatedDocs = (d1, d2) => d1.jdocid === d2.jdocid;

const jobRelatedDocSlice = createSlice({
    name: 'jobRelatedDoc',
    initialState: {
        data: {},
        docData: {}
    },
    reducers: {
        set: (state, action) => {
            let data = action.payload;
            if (!Array.isArray(data)) {
                data = [data];
            }
            data.forEach(dt => {
                delete state.data[dt.jobid?.toString()];
            })
            data.forEach(dt => {
                const jobid = dt.jobid?.toString();
                if (!state.data[jobid]) {
                    state.data[jobid] = []
                }
                state.data[jobid].push(dt);
            });
        },
        addOrUpdate: (state, action) => {
            for (const doc of ensureArray(action.payload)) {
                const jobid = doc.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareReleatedDocs(a, doc));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1, doc);
                    } else {
                        state.data[jobid].push(doc);
                    }
                } else {
                    state.data[jobid] = [doc];
                }
            }
        },
        remove: (state, action) => {
            for (const doc of ensureArray(action.payload)) {
                const jobid = doc.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareReleatedDocs(a, doc));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobRelatedDocSlice.getInitialState());
    }
});

// #region network operations
export const fetchJobRelatedDocs = jobid => network => network.get('/api/v1/jobRelatedDocs/' + encodeURIComponent(jobid), jobRelatedDocSlice.actions.set);
export const getJobRelatedDocData = async (network, id) => {
    const data = await network.fetchData('/api/v1/jobRelatedDoc/' + encodeURIComponent(id) + '/file');
    return await data.blob();
}
export const addRelatedDoc = (network, doc) => network.post('/api/v1/jobRelatedDoc', doc, jobRelatedDocSlice.actions.addOrUpdate);
export const removeRelatedDoc = (network, doc) => network.delete('/api/v1/jobRelatedDoc/' + encodeURIComponent(doc.jdocid), jobRelatedDocSlice.actions.remove);
// #endregion

// #region selectors
const selectRelatedDocs = state => state.jobRelatedDoc.data;

const EMPTY_ARRAY = [];
// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectRelatedDocsByJobID = createSelector([selectRelatedDocs, (docs, jobid) => jobid], (docs, jobid) => {
    return docs[jobid?.toString()] ?? EMPTY_ARRAY
});
const selectJobRelatedDocs = jobid => state => selectRelatedDocsByJobID(state, jobid);
// #endregion

// #region listeners
const listen = {
    jobRelatedDocCreated: (doc, { dispatch }) => dispatch(jobRelatedDocSlice.actions.addOrUpdate(doc)),
    jobRelatedDocUpdated: (doc, { dispatch }) => dispatch(jobRelatedDocSlice.actions.addOrUpdate(doc)),
    jobRelatedDocRemoved: (doc, { dispatch }) => dispatch(jobRelatedDocSlice.actions.remove(doc))
}
// #endregion

// make sure we don't keep making new objects etc to reduce re-rendering and effect cascades
export const [useJobRelatedDocs, realizeJobRelatedDocs] = dataHookFactory(selectJobRelatedDocs, initMapFactory(fetchJobRelatedDocs, listen, jobid => jobid?.toString()));

const reducerDetails = { name: jobRelatedDocSlice.name, reducer: jobRelatedDocSlice.reducer };
export default reducerDetails;